/** Draft-edit overrides for /notification_test_render/ — shared by the
 *  TestRenderDialog and the docked LivePreviewPane so both preview the exact
 *  same unsaved state through the same funnel.
 */
import { channelSpec } from '../delivery/channelFields';
import type { NotificationDraft } from '../useNotificationDraft';

export interface DraftOverrides {
  fieldOverrides: Record<string, unknown> | null;
  notificationOverrides: Record<string, unknown> | null;
}

/** Draft edits for the targeted DM that the test-render endpoint accepts:
 *  template sources, rendering types, template FK and theme — plus the
 *  notification-level disable_default_blocks / language_policy. */
export function buildOverrides(
  draft: NotificationDraft,
  deliveryMethodId: number | undefined,
): DraftOverrides {
  if (deliveryMethodId == null) return { fieldOverrides: null, notificationOverrides: null };
  const dm = draft.draft.deliveryMethods.find((d) => d.id === deliveryMethodId);
  if (!dm) return { fieldOverrides: null, notificationOverrides: null };

  const spec = channelSpec(dm.method);
  const allowed = new Set<string>([...spec.templateFields.map((f) => f.field), 'theme']);
  if (spec.renderingTypeField) allowed.add(spec.renderingTypeField);
  if (spec.templateField) allowed.add(spec.templateField);

  const prefix = `delivery_methods[${deliveryMethodId}].`;
  const fieldOverrides: Record<string, unknown> = {};
  for (const path of draft.dirtyPaths) {
    if (!path.startsWith(prefix)) continue;
    const field = path.slice(prefix.length);
    if (allowed.has(field)) fieldOverrides[field] = dm[field];
  }

  const notificationOverrides: Record<string, unknown> = {};
  if (draft.dirtyPaths.has('notification.disable_default_blocks')) {
    notificationOverrides.disable_default_blocks = draft.draft.meta.disable_default_blocks;
  }
  if (draft.dirtyPaths.has('notification.language_policy')) {
    notificationOverrides.language_policy = draft.draft.meta.language_policy;
  }

  return {
    fieldOverrides: Object.keys(fieldOverrides).length > 0 ? fieldOverrides : null,
    notificationOverrides:
      Object.keys(notificationOverrides).length > 0 ? notificationOverrides : null,
  };
}
